import type { SubjectUpdateFailedAction } from '@Application/Subjects/Actions/subjectUpdateFailed';
import UpdateSubject from '@Application/Subjects/Commands/UpdateSubject';
import updateSubject from '@Application/Subjects/updateSubject';
import SubjectSheetFormData from '@UI/Pages/Subjects/Shared/FormData/SubjectSheetFormData';
import SheetFields from '@UI/Pages/Subjects/Sidebars/CreateSubjectSidebar/SheetFields';
import editSubjectSidebarSignal, {
	closeEditSubjectSidebar,
} from '@UI/Pages/Subjects/Signals/editSubjectSidebarSignal';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import type SubjectView from '@UI/Store/Subjects/Views/SubjectView';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import { Button, Form, Sidebar } from '@verclam/ui-kit';

type Props = {
	readonly subject: SubjectView;
	readonly type: SubjectTypeView;
};

const PANEL_TITLE = 'Modifier la fiche';

/** Edit panel of the title and the sheet of a dossier, filled with its current values. */
const EditSubjectSidebar: React.FC<Props> = ({ subject, type }) => {
	const dispatch = useDispatch();
	const isVisible = useSignalValue(editSubjectSidebarSignal);

	const createSheet = React.useCallback(
		() => SubjectSheetFormData.createFor(type.fields, subject.title ?? '', subject.values),
		[type.fields, subject.title, subject.values],
	);

	const [sheet, setSheet] = React.useState(createSheet);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();

	React.useEffect(() => {
		if (isVisible) {
			setSheet(createSheet());
		}
	}, [isVisible, createSheet]);

	const handleSheetChange = React.useCallback(
		(update: (current: SubjectSheetFormData) => SubjectSheetFormData) => {
			setSheet(update);
		},
		[],
	);

	const handleSubmit = React.useCallback(() => {
		if (!sheet.isFormDataValid()) {
			setSheet(sheet.showErrors());
			return;
		}

		setIsSubmitting(true);

		dispatch(
			updateSubject(
				new UpdateSubject(subject.facilityId, subject.id, sheet.title.title.trim(), sheet.toValues()),
			),
		)
			.then(closeEditSubjectSidebar)
			.catch((action: SubjectUpdateFailedAction) => {
				setSheet((current) => current.applyServerErrors(action.payload?.fieldErrors ?? []));
			})
			.finally(() => setIsSubmitting(false));
	}, [sheet, subject.facilityId, subject.id, dispatch]);

	return (
		<Sidebar
			visible={isVisible}
			onHide={closeEditSubjectSidebar}
			position="right"
			header={PANEL_TITLE}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeEditSubjectSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={PANEL_TITLE}>
				<SheetFields
					titleLabel={type.wording.titleLabel}
					sheet={sheet}
					onSheetChange={handleSheetChange}
					autoFocusTitle
				/>
			</Form>
		</Sidebar>
	);
};

export default EditSubjectSidebar;
